// Current school endpoints. The old getSchool action is a separate demo API.
import { ADMIN_API_URL } from '../../config/api';

export const SCHOOL_SUMMARY_URL = process.env.REACT_APP_SCHOOL_SUMMARY_URL
  || ADMIN_API_URL;

export class SchoolRequestError extends Error {
  constructor(status, code = 'request_failed') {
    super('School request failed');
    this.name = 'SchoolRequestError';
    this.status = status;
    this.code = code;
  }
}

// getSchoolDirectory / getSignInStatus / getAccommodations (2026-09-25) serve
// the Students, Teachers, Levels, Usage and Accommodations pages. Read-only.
const SCHOOL_ACTIONS = ['getSchoolSummary', 'getSchoolContext', 'getSchoolOverview', 'getSchoolDirectory', 'getSignInStatus', 'getAccommodations'];

function aborted() {
  return new DOMException('Request cancelled', 'AbortError');
}

// refreshToken returns the new access token. Retry explicitly so Content-Type,
// the original action and cancellation survive the retry.
export async function requestSchool(action, { getAuth, signal, isCurrent = () => true }) {
  if (!SCHOOL_ACTIONS.includes(action)) throw new SchoolRequestError(400);
  const alive = () => {
    if (signal?.aborted || !isCurrent()) throw aborted();
  };
  alive();
  const auth = getAuth();
  if (!auth?.isLoggedIn || !auth.user?.accessToken) throw new SchoolRequestError(401, 'sign_in_required');
  const body = { action };
  const post = token => fetch(SCHOOL_SUMMARY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
    signal,
  });
  let response = await post(auth.user.accessToken);
  alive();
  if (response.status === 401 && auth.refreshToken) {
    const token = await auth.refreshToken();
    alive();
    if (!token) throw new SchoolRequestError(401, 'sign_in_required');
    response = await post(token);
    alive();
  }
  let result;
  try { result = await response.json(); } catch { throw new SchoolRequestError(response.status || 502, 'invalid_response'); }
  alive();
  if (!response.ok) throw new SchoolRequestError(response.status, typeof result?.code === 'string' ? result.code : 'request_failed');
  if (result?.contractVersion !== 1 || typeof result?.school?.id !== 'string' || !result.school.id.trim() || typeof result?.school?.name !== 'string' || !result.school.name.trim()) {
    throw new SchoolRequestError(502, 'invalid_response');
  }
  if (action === 'getSchoolSummary' && (
    !/^\d{4}-\d{2}-\d{2}$/.test(result.dataThrough || '') || !result.periods?.year || !result.periods?.recent
    || !Array.isArray(result.roster?.byGrade) || !Array.isArray(result.activity?.byGrade)
    || !Array.isArray(result.domains?.items) || !result.coverage
  )) throw new SchoolRequestError(502, 'invalid_response');
  if (action === 'getSchoolOverview') {
    const validRows = rows => Array.isArray(rows) && rows.every(row => typeof row?.id === 'string' && row.id.trim() && typeof row.name === 'string') && new Set(rows.map(row => row.id)).size === rows.length;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(result.dataThrough || '')
      || !validRows(result.teachers) || !validRows(result.classes) || !validRows(result.students)
      || !result.classes.every(row => typeof row.teacherId === 'string' && Array.isArray(row.studentIds))
      || !result.students.every(row => row.activity && row.domains && Array.isArray(row.domains.items))
      || !Array.isArray(result.domainDefinitions) || !result.periods?.recent?.start || !result.periods?.recent?.end
      || !result.coverage?.students || !result.coverage?.teachers || !result.coverage?.classes || !result.coverage?.enrollments
    ) throw new SchoolRequestError(502, 'invalid_response');
  }
  if (action === 'getSchoolDirectory') {
    const ids = rows => Array.isArray(rows) && rows.every(row => typeof row?.id === 'string' && row.id.trim()) && new Set(rows.map(row => row.id)).size === rows.length;
    if (!ids(result.students) || !ids(result.teachers) || !ids(result.classes)
      || !Array.isArray(result.domainOrder) || !Array.isArray(result.series?.days) || !result.series.school || !result.coverage
      || !result.students.every(row => Array.isArray(row.classIds))
    ) throw new SchoolRequestError(502, 'invalid_response');
  }
  if (action === 'getSignInStatus' && (!Array.isArray(result.temporaryPassword?.students) || !Array.isArray(result.temporaryPassword?.teachers))) {
    throw new SchoolRequestError(502, 'invalid_response');
  }
  if (action === 'getAccommodations' && (!result.extendedTime || !Array.isArray(result.extendedTime.students) || !result.textToSpeech || !Array.isArray(result.textToSpeech.studentIds))) {
    throw new SchoolRequestError(502, 'invalid_response');
  }
  return result;
}

// The refresh token distinguishes separate logins by the same account. It is
// only used in memory and is never logged or persisted by the dashboard.
export function schoolSessionKey(auth) {
  if (!auth?.isLoggedIn || !auth.user) return null;
  const user = auth.user;
  return JSON.stringify([user.username || user.sub || '', user.refreshToken || user.accessToken || '', user.role || '', user.school || '']);
}
